// Review cards for the product page (sections/product-reviews.liquid).
//
// Judge.me keeps only the count and the average in Shopify, so the texts are read from the same Judge.me endpoint its
// own widget uses. Imported reviews are included there. No token is needed, the endpoint is public and cached by
// Judge.me for 20 minutes.
//
// The request starts when the customer scrolls close to the section, so it never competes with the top of the product
// page. Star rows and the verified badge come from templates rendered by Liquid, and the review text is inserted as
// text, never as HTML.

const API_URL = 'https://api.judge.me/reviews/reviews_for_widget';
const MAX_PER_PAGE = 30;
const REQUEST_TIMEOUT = 8000;
const ROOT_MARGIN = '600px 0px';

const parseBody = (html) => {
  const document_ = new DOMParser().parseFromString(html || '', 'text/html');

  document_.querySelectorAll('br').forEach((lineBreak) => lineBreak.replaceWith('\n'));
  document_.querySelectorAll('p, div, li').forEach((block) => block.append('\n'));

  return (document_.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

// Returns the date in the shop time zone as { iso: "2025-12-19", display: "19.12.2025" }
const formatDate = (date, timeZone) => {
  let iso;

  try {
    // en-CA formats as YYYY-MM-DD
    iso = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  } catch {
    // Unknown time zone: fall back to the visitor's own
    iso = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  }

  const [year, month, day] = iso.split('-');

  return { iso, display: `${day}.${month}.${year}` };
};

class ProductReviewList extends HTMLElement {
  connectedCallback() {
    if (this.loaded) {
      return;
    }

    this.loaded = true;

    if (!window.IntersectionObserver) {
      this.load();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          this.load();
        }
      },
      { rootMargin: ROOT_MARGIN }
    );

    observer.observe(this);
  }

  async load() {
    try {
      const reviews = await this.fetchReviews();
      this.render(reviews);
    } catch (error) {
      console.warn('[Reviews] Could not load the reviews from Judge.me:', error);
      this.hidden = true;
    } finally {
      this.classList.remove('product-review-list--loading');
    }
  }

  async fetchReviews() {
    const shopDomain = this.getAttribute('shop-domain');
    const productId = this.getAttribute('product-id');

    if (!shopDomain || !productId) {
      return [];
    }

    const url = new URL(API_URL);

    url.search = new URLSearchParams({
      url: shopDomain,
      shop_domain: shopDomain,
      platform: 'shopify',
      product_id: productId,
      page: '1',
      // A few reviews more than needed, because reviews without a text are dropped below
      per_page: String(Math.min(MAX_PER_PAGE, this.limit + 4)),
      sort_by: 'rating',
      sort_dir: 'desc'
    });

    const response = await fetch(url, { signal: AbortSignal.timeout(REQUEST_TIMEOUT) });

    if (!response.ok) {
      throw new Error(`Judge.me returned HTTP ${response.status}`);
    }

    const payload = await response.json();

    return Array.isArray(payload.reviews) ? payload.reviews : [];
  }

  // A review is shown when it has a rating, a date and a text. The author may be missing or anonymous.
  render(reviews) {
    const cards = [];

    for (const review of reviews) {
      if (cards.length === this.limit) {
        break;
      }

      const rating = Math.round(Number(review.rating));
      const date = new Date(review.created_at);
      const body = parseBody(review.body_html);

      if (!(rating >= 1 && rating <= 5) || Number.isNaN(date.valueOf()) || body === '') {
        continue;
      }

      cards.push(this.buildCard({ rating, date, body, review }));
    }

    if (cards.length === 0) {
      this.hidden = true;
      return;
    }

    const slider = this.querySelector('template[data-review-slider]');

    if (!slider) {
      return;
    }

    // The cards go in before the slider enters the page: scroll-carousel only wires up its scroll listeners (which
    // drive the arrows) when it is created with more than one cell.
    const fragment = slider.content.cloneNode(true);
    const carousel = fragment.querySelector('scroll-carousel');

    carousel.append(...cards);
    this.append(fragment);

    this.setupDrag(this.querySelector('scroll-carousel'));
  }

  // Dragging with the mouse. The carousel stays where it is released, and the arrows keep aligning to a card, because
  // they scroll to a cell offset on their own.
  setupDrag(carousel) {
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let moved = false;

    carousel.addEventListener('pointerdown', (event) => {
      // Touch and pen keep their native scrolling
      if (event.pointerType !== 'mouse' || event.button !== 0) {
        return;
      }

      dragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = carousel.scrollLeft;

      try {
        // Keeps the moves coming even when the cursor leaves the carousel
        carousel.setPointerCapture(event.pointerId);
      } catch {
        // Capture is a convenience, dragging works without it
      }

      carousel.classList.add('is-dragging');
    });

    carousel.addEventListener('pointermove', (event) => {
      if (!dragging) {
        return;
      }

      const distance = event.clientX - startX;

      if (Math.abs(distance) > 3) {
        moved = true;
      }

      carousel.scrollLeft = startScrollLeft - distance;
      event.preventDefault();
    });

    const stopDragging = (event) => {
      if (!dragging) {
        return;
      }

      dragging = false;
      carousel.classList.remove('is-dragging');
      carousel.releasePointerCapture?.(event.pointerId);
    };

    carousel.addEventListener('pointerup', stopDragging);
    carousel.addEventListener('pointercancel', stopDragging);

    // A drag that ends on a link or a button must not count as a click
    carousel.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      { capture: true }
    );
  }

  buildCard({ rating, date, body, review }) {
    const card = document.createElement('article');
    card.className = 'review-card snap-start';

    const stars = this.querySelector(`template[data-review-stars="${rating}"]`);

    if (stars) {
      card.append(stars.content.cloneNode(true));
    }

    const text = document.createElement('p');
    text.className = 'review-card__body';
    text.textContent = body;
    card.append(text);

    const footer = document.createElement('footer');
    footer.className = 'review-card__footer';

    const author = (review.reviewer_name || '').trim();

    if (author !== '') {
      const authorElement = document.createElement('p');
      authorElement.className = 'review-card__author h6';
      authorElement.textContent = author;
      footer.append(authorElement);
    }

    const meta = document.createElement('p');
    meta.className = 'review-card__meta text-xs text-subdued';

    const formattedDate = formatDate(date, this.getAttribute('time-zone'));
    const time = document.createElement('time');
    time.dateTime = formattedDate.iso;
    time.textContent = formattedDate.display;
    meta.append(time);

    const verifiedBadge = this.querySelector('template[data-review-verified]');

    if (review.verified_buyer && verifiedBadge) {
      meta.append(verifiedBadge.content.cloneNode(true));
    }

    footer.append(meta);
    card.append(footer);

    return card;
  }

  get limit() {
    return Number(this.getAttribute('limit')) || 12;
  }
}

if (!window.customElements.get('product-review-list')) {
  window.customElements.define('product-review-list', ProductReviewList);
}
